'use client';

import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { getBadges, updateCelebrated } from '@src/actions/gamification.actions';
import { BadgeCelebrationAnalytics } from '@src/components/BadgeCelebration/BadgeCelebrationAnalytics';
import { BadgeShareModal } from '@src/components/BadgeShare/BadgeShareModal';
import { useSession } from '@src/hooks/useSession';
import { useLocale } from '@src/i18n/LocaleProvider';
import { isAppHomepagePath } from '@src/i18n/locale.util';
import { getUncelebratedBadges } from '@src/utils/gamification.util';
import { instrumentAction } from "@src/utils/instrumentation";
const UNCELEBRATED_BADGES_KEY = 'uncelebrated-badges';
interface BadgeCelebrationProviderProps {
  children: ReactNode;
}
export function BadgeCelebrationProvider(t0) {
  const $ = _c(37);
  const {
    children
  } = t0;
  const {
    user,
    authSession
  } = useSession();
  const {
    locale
  } = useLocale();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const guid = user?.guid;
  let t1;
  if ($[0] !== pathname) {
    t1 = isAppHomepagePath(pathname);
    $[0] = pathname;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isAppHomepage = t1;
  const isLoggedIn = !!guid && !!authSession?.access_token;
  const shouldAutoSync = isLoggedIn && isAppHomepage;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [queue, setQueue] = useState(t2);
  const celebrationBadge = queue[0] ?? null;
  let t3;
  if ($[3] !== guid || $[4] !== locale.code) {
    t3 = [UNCELEBRATED_BADGES_KEY, guid, locale.code];
    $[3] = guid;
    $[4] = locale.code;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const badgesQueryKey = t3;
  let t4;
  if ($[6] !== locale.code) {
    t4 = () => instrumentAction("GamificationActions.getBadges", getBadges)(locale.code);
    $[6] = locale.code;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== badgesQueryKey || $[9] !== shouldAutoSync || $[10] !== t4) {
    t5 = {
      queryKey: badgesQueryKey,
      queryFn: t4,
      enabled: shouldAutoSync,
      staleTime: 0,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false
    };
    $[8] = badgesQueryKey;
    $[9] = shouldAutoSync;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const {
    data
  } = useQuery(t5);
  const lastSeededRef = useRef(null);
  let t6;
  let t7;
  if ($[12] !== data) {
    t6 = () => {
      if (!data || data === lastSeededRef.current) {
        return;
      }
      lastSeededRef.current = data;
      const uncelebrated = getUncelebratedBadges(data.badges);
      if (uncelebrated.length === 0) {
        return;
      }
      setQueue(prev => prev.length ? prev : uncelebrated);
    };
    t7 = [data];
    $[12] = data;
    $[13] = t6;
    $[14] = t7;
  } else {
    t6 = $[13];
    t7 = $[14];
  }
  useEffect(t6, t7);
  let t8;
  if ($[15] !== locale.code) {
    t8 = async badge => {
      const result = await instrumentAction("GamificationActions.updateCelebrated", updateCelebrated)(badge.id, locale.code, badge.slug);
      if (result.id == null) {
        throw new Error(`updateCelebrated returned no id for badge ${badge.id}`);
      }
      return result;
    };
    $[15] = locale.code;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== badgesQueryKey || $[18] !== queryClient) {
    t9 = () => {
      queryClient.invalidateQueries({
        queryKey: badgesQueryKey
      });
    };
    $[17] = badgesQueryKey;
    $[18] = queryClient;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = (_, badge_0) => {
      setQueue(prev_0 => [badge_0, ...prev_0.filter(b => b.id !== badge_0.id)]);
    };
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t8 || $[22] !== t9) {
    t11 = {
      mutationFn: t8,
      onSuccess: t9,
      onError: t10
    };
    $[21] = t8;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const {
    mutate: markCelebrated
  } = useMutation(t11);
  let t12;
  if ($[24] !== celebrationBadge || $[25] !== markCelebrated) {
    t12 = () => {
      if (!celebrationBadge) {
        return;
      }
      setQueue(prev_1 => prev_1.filter(b_0 => b_0.id !== celebrationBadge.id));
      markCelebrated(celebrationBadge);
    };
    $[24] = celebrationBadge;
    $[25] = markCelebrated;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const handleClose = t12;
  const t13 = isAppHomepage ? celebrationBadge : null;
  let t14;
  if ($[27] !== t13) {
    t14 = <BadgeCelebrationAnalytics badge={t13} />;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const t15 = isAppHomepage && !!celebrationBadge;
  let t16;
  if ($[29] !== celebrationBadge || $[30] !== handleClose || $[31] !== t15) {
    t16 = <BadgeShareModal isOpen={t15} badge={celebrationBadge} onClose={handleClose} />;
    $[29] = celebrationBadge;
    $[30] = handleClose;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== children || $[34] !== t14 || $[35] !== t16) {
    t17 = <>{children}{t14}{t16}</>;
    $[33] = children;
    $[34] = t14;
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  return t17;
}